import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { AppDispatch } from '@/app/store/store';
import { executeQuery } from '@/shared/features/query/utils/queryUtils';
import { getPageApiBaseUrl } from '@/core/utils/apiUtils';
import type { PdpResponseDTO } from '../dto/pdp.dto';
import { pdpDtoToProductDetail, productDetailToPlainObject, plainObjectToProductDetail } from '../mappers/pdp.mapper';
import type { ProductDetailRepository, ProductDetailParams } from '../../domain/interfaces/ProductDetailRepository';
import type { ProductDetail } from '../../domain/models/ProductDetail';

export type GetPdpArgs = {
  designId: string;
  customizationId?: number;
  size?: string;
};

const pdpApiSlice = createApiSlice({
  clientId: 'pdpApi',
  tagTypes: ['PDP'],
  endpoints: {
    getProductDetail: {
      url: (args: unknown) => {
        const { designId, customizationId, size } = (args || {}) as GetPdpArgs;
        const base = `${getPageApiBaseUrl()}/page/product/${designId}`;
        const params: string[] = [];
        if (customizationId) params.push(`customization_id=${customizationId}`);
        if (size) params.push(`size=${encodeURIComponent(size)}`);
        return params.length ? `${base}?${params.join('&')}` : base;
      },
      method: 'GET',
      transformResponse: (response: unknown): Record<string, unknown> => {
        const dto = (response ?? {}) as PdpResponseDTO;
        const domain = pdpDtoToProductDetail(dto);
        return productDetailToPlainObject(domain);
      },
      keepUnusedDataFor: 300,
      providesTags: ['PDP'],
    },
  },
});

export const pdpApi = pdpApiSlice.queryClient;
export const pdpApiRedux = pdpApiSlice.queryClient as ReduxQueryClient;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK Query hooks types are complex
const hooks = pdpApiSlice.hooks as any;
export const useGetProductDetailQuery: (
  args: GetPdpArgs,
  opts?: { refetchOnMountOrArgChange?: boolean; skip?: boolean }
) => {
  data: Record<string, unknown> | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  isUninitialized: boolean;
  error: unknown;
} =
  hooks?.useGetProductDetailQuery ??
  (() => ({
    data: undefined,
    isLoading: false,
    isFetching: false,
    isError: false,
    isSuccess: false,
    isUninitialized: true,
    error: null,
  }));

export class PdpAdapter implements ProductDetailRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async fetchProductDetail(designId: string, params?: ProductDetailParams): Promise<ProductDetail> {
    const initiateResult = pdpApi.endpoints.getProductDetail.initiate({
      designId,
      customizationId: params?.customizationId,
      size: params?.size,
    });
    const plain = await executeQuery<Record<string, unknown>>(initiateResult, this.dispatch);
    return plainObjectToProductDetail(plain);
  }
}
